import { defineStore } from 'pinia';
import { computed } from 'vue';

import { COMPETITIVE_AVERAGE_KEY } from '@/app/dashboards/constants';
import { CREATOR_INVITATION_STATUS_LABELS, CREATOR_STATUS } from '@/app/dashboards/reportConstants';
import { getAdsPlatformController } from '@/composables/platform/getAdsPlatformController';
import { BRAND } from '@/models/auth/permissions.enum';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { useMetricsStore } from '@/stores/metrics';

export const useDashboardPermissionsStore = defineStore('dashboardPermissions', () => {
  const identityStore = useIdentityStore();
  const flagStore = useFlagStore();
  const metricsStore = useMetricsStore();

  function canUserAccessDashboards(brand) {
    const brandLabel = brand.label;
    return identityStore.identity?.permissions?.dashboard?.[brandLabel]?.can_access_dashboards;
  }
  const canAccessCustomMetrics = computed(() => {
    const brandHasAccessToCustomMetrics = identityStore.guard(
      BRAND.SETTINGS.CAN_ACCESS_CUSTOM_METRICS,
    );
    return brandHasAccessToCustomMetrics;
  });

  function canUserAccessCompetitors(brand) {
    const brandLabel = brand?.label;
    return identityStore.identity?.permissions?.competitive?.[brandLabel]?.can_access_competitors;
  }

  const canAccessDashboards = computed(() =>
    identityStore.allUserBrands.some(
      (brand) =>
        identityStore.guard(BRAND.DASHBOARD.CAN_ACCESS_DASHBOARDS, brand) ||
        canUserAccessDashboards(brand) ||
        false,
    ),
  );
  // TODO: remove when removing feature flag  https://app.shortcut.com/dashhudson/story/155883/remove-gdi-dashboard-feature-flag
  // Make sure all the components that use this logic has been removed first or replaced with the new logic now inside the useDashboards composable
  const canAccessIndustryBenchmarks = computed(() =>
    identityStore.allUserBrands.some(
      (brand) =>
        identityStore.guard(BRAND.DASHBOARD.CAN_ACCESS_INDUSTRY_BENCHMARKS, brand) || false,
    ),
  );

  function canAccessMetrics(channel, metric, metricTypeReportKey, brands = null) {
    const { permissions } =
      metricsStore.getTargetMetricDetails(channel, metricTypeReportKey, metric) ?? {};
    return identityStore.checkIfBrandPermissionsExist(permissions, brands);
  }

  function canAccessCompetitor(competitor) {
    if (competitor?.isCompetitor && competitor?.id !== COMPETITIVE_AVERAGE_KEY) {
      const brandDetails = competitor.brandDetails ?? [];
      return !!brandDetails.some(
        (brand) =>
          canUserAccessCompetitors(identityStore.identityBrandsById[brand.brandId]) ||
          identityStore.guard(
            BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS,
            identityStore.identityBrandsById[brand.brandId],
          ),
      );
    }
    return true;
  }

  function userOrBrandCanAccessCompetitor(brand) {
    return (
      identityStore.guard(BRAND.COMPETITIVE.CAN_ACCESS_COMPETITORS, brand) ||
      canUserAccessCompetitors(brand)
    );
  }

  function userOrBrandCanAccessDashboards(brand) {
    return (
      identityStore.guard(BRAND.DASHBOARD.CAN_ACCESS_DASHBOARDS, brand) ||
      canUserAccessDashboards(brand)
    );
  }

  function brandCanAccessBenchmark(brand) {
    return identityStore.guard(BRAND.DASHBOARD.CAN_ACCESS_INDUSTRY_BENCHMARKS, brand);
  }

  function canAccessContentTags(selectedBrandsOrCompetitors) {
    return identityStore.checkIfBrandPermissionsExist(
      [BRAND.TAGS.CAN_ACCESS_CONTENT_TAGS],
      selectedBrandsOrCompetitors,
    );
  }

  function brandCanAccessAds(brand, channel) {
    const adsPlatformController = getAdsPlatformController(channel);
    return adsPlatformController?.brandHasPermission(brand) ?? false;
  }

  function userCanAccessAds(brand, channel) {
    const adsPlatformController = getAdsPlatformController(channel);
    return adsPlatformController?.userHasPermission(brand) ?? false;
  }

  function canAccessAdsForBrand(brand, channel) {
    return brandCanAccessAds(brand, channel) && userCanAccessAds(brand, channel);
  }

  function canAccessTemplatePermissions(template) {
    return (
      identityStore.checkIfBrandPermissionsExist(template.brandPermissions) ||
      identityStore.checkIfUserPermissionsExist(template.userPermissions)
    );
  }

  function canAccessTemplateFlags(template) {
    if (template?.requiredFlag) {
      return flagStore.flags[template.requiredFlag];
    }
    if (template?.hiddenRequiredFlag) {
      return !flagStore.flags[template.hiddenRequiredFlag];
    }
    return true;
  }

  function creatorIsConnected(creator) {
    const isConnected = creator?.connection_status === true;
    const invitationStatus = creator?.invitation_status;
    if (invitationStatus !== undefined && invitationStatus !== null) {
      const invitationLabel =
        CREATOR_INVITATION_STATUS_LABELS[invitationStatus] ||
        CREATOR_INVITATION_STATUS_LABELS.default;
      const isAccepted = invitationLabel === CREATOR_STATUS.CONNECTED;
      return isConnected && isAccepted;
    }
    return isConnected;
  }

  return {
    canUserAccessDashboards,
    canUserAccessCompetitors,
    canAccessMetrics,
    canAccessDashboards,
    canAccessIndustryBenchmarks,
    canAccessCompetitor,
    userOrBrandCanAccessCompetitor,
    brandCanAccessBenchmark,
    userOrBrandCanAccessDashboards,
    canAccessContentTags,
    canAccessAdsForBrand,
    brandCanAccessAds,
    userCanAccessAds,
    canAccessTemplatePermissions,
    canAccessTemplateFlags,
    canAccessCustomMetrics,
    creatorIsConnected,
  };
});
